import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import errorResponses from '../../../errorResponses';
import useGlynt from '../../hooks/useGlynt';
import { AppContext } from '../../../App';
import { saveAs } from 'file-saver';
import { useQuery } from '@tanstack/react-query';
import CloudDownload from '@mui/icons-material/CloudDownload';
import { useTheme } from '@mui/material/styles';

interface DownloadExtractionProps {
  failed: boolean;
  extractionId: string;
  docLabel: string | undefined;
}

const DownloadExtraction: React.FC<DownloadExtractionProps> = ({
  failed,
  extractionId,
  docLabel = 'loading',
}) => {
  const { setToast } = React.useContext(AppContext);
  const [open, setOpen] = React.useState(false);
  const [reportType, setReportType] = React.useState('transformed');
  const glynt = useGlynt();
  const { refetch } = useQuery({
    queryKey: ['extraction', extractionId, { view: 'final' }],
    queryFn: async () => await (await glynt).extraction.retrieve(extractionId),
    enabled: false
  });
  const { refetch: refetchTransform } = useQuery({
    queryKey: ['transformerExtraction'],
    queryFn: async () => await (await glynt).extraction.transformer(extractionId, { format: 'json' }),
    enabled: false
  });
  const theme = useTheme();
  const handleDownload = () => {
    setToast({
      variant: 'success',
      open: true,
      message: 'Downloading JSON',
    });
    if (reportType === 'transformed' && !failed) {
      let message =
        'An error occurred while retrieving transformed data. Please try again or contact your Glynt representative.';

      refetchTransform()
        .then((data) => {
          // console.log({ data });
          if (data.isSuccess) {
            // @ts-ignore
            var blob = new Blob([JSON.stringify(data.data)], {
              type: 'application/json;charset=utf-8',
            });
            saveAs(blob, `${docLabel}-extract-report-${extractionId}.json`);
            setToast({ open: false });
          } else {
            if (
              data.error instanceof Error &&
              data.error.message.includes('Transformation not found')
            ) {
              message = errorResponses.transformationNotFound;
            }
            setToast({
              variant: 'error',
              open: true,
              message,
            });
          }
        })
        .catch(() => {
          setToast({
            variant: 'error',
            open: true,
            message,
          });
        })
        .finally(() => setOpen(false));
    } else {
      refetch()
        .then((data) => {
          // console.log({ data });
          // @ts-ignore
          var blob = new Blob([JSON.stringify(data.data.results)], {
            type: 'application/json;charset=utf-8',
          });
          saveAs(blob, `${docLabel}-extract-report-${extractionId}.json`);
        })
        .catch(() => {
          setToast({
            variant: 'error',
            open: true,
            message: 'Something went wrong during download',
          });
        })
        .finally(() => setOpen(false));
    }
  };

  return (
    <>
      <Tooltip
        placement="top"
        title={`Download Results`}
        disableFocusListener
        disableTouchListener
        PopperProps={{
          popperOptions: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 0],
                },
              },
            ],
          },
        }}
      >
        <CloudDownload
          data-cy={`btn-download-results-${docLabel}`}
          onClick={() => {
            !failed ? setOpen(true) : handleDownload();
          }}
          style={{ color: theme.palette.custom.lightGreen }}
        />
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Download JSON</DialogTitle>
        <DialogContent>
          <Grid container spacing={6}>
            <Grid item xs>
              <FormControl fullWidth>
                <InputLabel htmlFor="report-type">Report Type</InputLabel>
                <Select
                  value={reportType}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (typeof value === 'string') {
                      setReportType(value);
                    }
                  }}
                  inputProps={{
                    name: 'report-type',
                    id: 'report-type',
                  }}
                  data-cy="dialog-report-type-dropdown"
                >
                  <MenuItem value={'transformed'}>TRANSFORMED</MenuItem>
                  <MenuItem value={'default'} data-cy="dropdown-report-type-item-default">
                    DEFAULT
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            id="extraction-batches-dialog-cancel"
            onClick={() => setOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            id="extraction-batches-dialog-download"
            onClick={handleDownload}
            data-cy="btn-download-report-type"
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DownloadExtraction;
